import { isWorkflowRunAskId } from '@digital-twins/shared-types'

/**
 * THE workflow-run ask-id predicate. Matches every persisted workflow-run
 * line: ask, step, gate, failed, cancelled, complete. Every caller that needs
 * to ask "is this a run line?" reads this one rather than hand-rolling a
 * `startsWith`. The namespace itself lives in shared-types, beside the
 * prefixes the backend writes.
 */
export { isWorkflowRunAskId }

/**
 * The two names a thread turn can answer to.
 *
 * A turn sent from this page mints a local `newId()` id and keeps it for the
 * whole session: the plan cursor, the streaming updates and the park effect
 * all key off that id, so renaming it mid-flight would detach a plan card
 * from its own message. The server names the same turn with a message token,
 * which is what a later `listMessages` returns. `serverToken` is that second
 * name, learned from the `done` frame, and it is the only thing that lets the
 * two id spaces meet (D0, thread-output-continuity).
 */
export interface ReconcilableTurn {
  id: string
  serverToken?: string
  askId?: string | null
  createdAt?: string
}

function insertExtras<T extends ReconcilableTurn>(
  prev: Array<T>,
  extras: Array<T>,
): Array<T> {
  const dated: Array<T> = []
  const undated: Array<T> = []
  for (const extra of extras) {
    if (extra.createdAt) {
      dated.push(extra)
    } else {
      undated.push(extra)
    }
  }
  dated.sort((left, right) => {
    return (left.createdAt ?? '').localeCompare(right.createdAt ?? '')
  })

  if (dated.length === 0) {
    return [...prev, ...undated]
  }

  const next = [...prev]
  for (const extra of dated) {
    const extraTime = extra.createdAt as string
    let insertAt = next.length
    let sawDated = false
    for (let i = 0; i < next.length; i += 1) {
      const at = next[i]?.createdAt
      if (!at) {
        if (sawDated) {
          insertAt = i
          break
        }
        continue
      }
      sawDated = true
      if (at > extraTime) {
        insertAt = i
        break
      }
    }
    next.splice(insertAt, 0, extra)
  }
  return undated.length > 0 ? [...next, ...undated] : next
}

/**
 * Appends the workflow-run lines that are not on screen yet, and nothing
 * else.
 *
 * 1. **The askId filter is the guard that closes the bug.** This merge
 *    exists to pick up durable `workflow-run-*` rows a run persisted
 *    (complete, gate, failed, cancelled, and the launch ask). Every other
 *    message in the reload is either already rendered or belongs to a turn
 *    this page never sent, and appending those is what made a whole
 *    exchange appear twice (D0). Filtering incoming turns to that prefix,
 *    before anything else runs, is what stops the duplicate.
 * 2. **The `serverToken` half of the match is defensive plumbing, not load
 *    bearing.** A turn also matches if the incoming message's token equals
 *    its `serverToken` (learned from the `done` frame when this page sent
 *    it). No input in production exercises this branch: outcome lines are
 *    inserted by listeners that never run inside this page's own
 *    request/response cycle, so their messages never travel through a
 *    `done` frame this page receives. A locally sent turn's `serverToken`
 *    can therefore never equal an announcement's token. The tokens are
 *    kept and checked anyway because they are already on the wire from the
 *    D0 work, and a future consumer of this merge that appends
 *    non-announcement messages by token — rather than filtering to the
 *    prefix first — would need exactly this branch to recognise a turn it
 *    already sent.
 */
export function mergeRunCompletionAnnouncements<T extends ReconcilableTurn>(
  prev: Array<T>,
  incoming: Array<T>,
): Array<T> {
  const have = new Set(
    prev.flatMap((turn) =>
      turn.serverToken ? [turn.id, turn.serverToken] : [turn.id],
    ),
  )
  const haveAskIds = new Set(
    prev.flatMap((turn) => (turn.askId ? [turn.askId] : [])),
  )
  const extras = incoming
    .filter((turn) => isWorkflowRunAskId(turn.askId))
    .filter((turn) => !have.has(turn.id))
    .filter((turn) => !turn.askId || !haveAskIds.has(turn.askId))
  if (extras.length === 0) {
    return prev
  }
  return insertExtras(prev, extras)
}
